/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';

/**
 * Log levels available in the application
 */
export enum LogLevel {
  Off = 0,
  Error,
  Warning,
  Info,
  Debug,
}

/**
 * Service for centralized and configurable logging
 * Replaces direct console.log calls throughout the application
 */
@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  // Current log level - can be configured
  level: LogLevel = environment.production ? LogLevel.Error : LogLevel.Debug;

  /**
   * Log debug message - for detailed tracing info (development only)
   * @param message - Primary message to log
   * @param optionalParams - Additional parameters to log
   */
  debug(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.Debug, message, optionalParams);
  }

  /**
   * Log info message - for general application flow
   * @param message - Primary message to log
   * @param optionalParams - Additional parameters to log
   */
  info(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.Info, message, optionalParams);
  }

  /**
   * Log warning message - for potential issues
   * @param message - Primary message to log
   * @param optionalParams - Additional parameters to log
   */
  warn(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.Warning, message, optionalParams);
  }

  /**
   * Log error message - for actual errors
   * @param message - Primary message to log
   * @param optionalParams - Additional parameters to log
   */
  error(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.Error, message, optionalParams);
  }

  /**
   * Internal logging method
   * @param level - Log level
   * @param message - Primary message to log
   * @param optionalParams - Additional parameters to log
   */
  private log(level: LogLevel, message: string, optionalParams: any[]): void {
    if (level <= this.level) {
      const timestamp = new Date().toISOString();
      const logPrefix = `[${timestamp}] [${LogLevel[level]}]`;

      // Avoid logging in production depending on level
      if (environment.production && level < LogLevel.Error) {
        return;
      }

      switch (level) {
        case LogLevel.Debug:
          console.debug(logPrefix, message, ...optionalParams);
          break;
        case LogLevel.Info:
          console.info(logPrefix, message, ...optionalParams);
          break;
        case LogLevel.Warning:
          console.warn(logPrefix, message, ...optionalParams);
          break;
        case LogLevel.Error:
          console.error(logPrefix, message, ...optionalParams);
          break;
      }
    }
  }
}
